import { useCallback, useRef, useState } from 'react';
import { commentService, violationService } from '../../clients/ApiClients';
import { AttachmentReturn } from '../../clients/rust-api-client';
import { billingErrorMessage } from '../../utils/billingHelpers';
import { rejectionFor } from './attachmentRules';

/** What an attachment is being hung off. The HOA is derived from it. */
export type AttachmentTarget =
  | { kind: 'comment'; id: string }
  | { kind: 'violation'; id: string };

export type StagedStatus =
  | 'pending'
  | 'uploading'
  | 'done'
  /** Refused here, before any request. Never retried. */
  | 'rejected'
  /** The server refused it, or the network did. Retryable. */
  | 'failed';

export interface StagedFile {
  key: string;
  file: File;
  status: StagedStatus;
  message?: string;
}

let nextKey = 0;

const upload = async (
  target: AttachmentTarget,
  file: File
): Promise<AttachmentReturn> => {
  const response =
    target.kind === 'comment'
      ? await commentService.uploadCommentAttachment({ id: target.id, file })
      : await violationService.uploadViolationAttachment({
          id: target.id,
          file,
        });
  return response.data;
};

/**
 * Staging and uploading files for a comment or a violation.
 *
 * Upload is single-shot upload-and-attach, one file per request — there is
 * deliberately no upload-then-link API, because a two-step version orphans
 * files when step two never happens and lets a caller attach a file
 * uploaded against one HOA to a target in another.
 */
export const useAttachmentUploads = () => {
  const [staged, setStaged] = useState<StagedFile[]>([]);
  // Held in a ref as well so a sequential upload loop reads current state
  // rather than the snapshot it closed over.
  const stagedRef = useRef<StagedFile[]>([]);

  const commit = useCallback((next: StagedFile[]) => {
    stagedRef.current = next;
    setStaged(next);
  }, []);

  const patch = useCallback(
    (key: string, changes: Partial<StagedFile>) => {
      commit(
        stagedRef.current.map((f) => (f.key === key ? { ...f, ...changes } : f))
      );
    },
    [commit]
  );

  /** Validate and queue picked files. A refusal never leaves the browser. */
  const stage = useCallback(
    (files: FileList | File[]) => {
      const added: StagedFile[] = Array.from(files).map((file) => {
        const reason = rejectionFor(file);
        return {
          key: `file-${nextKey++}`,
          file,
          status: reason ? ('rejected' as const) : ('pending' as const),
          message: reason ?? undefined,
        };
      });
      commit([...stagedRef.current, ...added]);
    },
    [commit]
  );

  const remove = useCallback(
    (key: string) => {
      commit(stagedRef.current.filter((f) => f.key !== key));
    },
    [commit]
  );

  const reset = useCallback(() => commit([]), [commit]);

  /**
   * Upload everything still outstanding, one request at a time.
   *
   * Sequential rather than `Promise.all`: it keeps `sort_order`
   * deterministic and avoids several 25 MB bodies in flight at once.
   *
   * Returns true when nothing is left in a `failed` state, so the caller
   * can decide whether the composer may close.
   */
  const uploadAll = useCallback(
    async (target: AttachmentTarget): Promise<boolean> => {
      const outstanding = stagedRef.current.filter(
        (f) => f.status === 'pending' || f.status === 'failed'
      );

      for (const item of outstanding) {
        patch(item.key, { status: 'uploading', message: undefined });
        try {
          await upload(target, item.file);
          patch(item.key, { status: 'done' });
        } catch (err) {
          console.error('Attachment upload failed:', err);
          patch(item.key, {
            status: 'failed',
            message: billingErrorMessage(err, 'That file could not be attached.'),
          });
        }
      }

      return stagedRef.current.every((f) => f.status !== 'failed');
    },
    [patch]
  );

  const hasUploadable = staged.some(
    (f) => f.status === 'pending' || f.status === 'failed'
  );
  const hasFailures = staged.some((f) => f.status === 'failed');

  return { staged, stage, remove, reset, uploadAll, hasUploadable, hasFailures };
};
